// The photo review queue.
//
// This screen used to read `patient_media`, a table bound by foreign key to the legacy `cases`
// table — so it could never show the progress photos patients actually send, which are stored
// against workflow cases. It now reviews those: everything nobody has looked at yet, newest first.
//
// A reviewer is answering one question per photo — does this look like it is tracking, or does
// somebody need to call the clinic — so the queue gives them the image large, the case context
// beside it, and two buttons.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PHOTO_POSE_LABELS, formatDateTime, type PendingPhoto } from '@alignova/shared';
import { PageHeader, Card } from '../../ui/primitives.tsx';
import { SkeletonList } from '../../ui/mobile.tsx';
import { useCan } from '../../auth/useCan.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { IconAlert, IconCheck } from '../../ui/icons.tsx';
import { fileDownloadUrl, wfApi } from '../workflow/workflowApi.ts';
import { Btn, Empty, inputCls } from '../workflow/wfUi.tsx';

export function MediaReviewPage() {
  const can = useCan();
  const [items, setItems] = useState<PendingPhoto[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    wfApi
      .pendingPhotos()
      .then((r) => {
        setItems(r);
        setErr(null);
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(load, [load]);
  useLiveEvent('workflow.changed', load);

  return (
    <div>
      <PageHeader
        title="Photo review"
        subtitle="Progress photos patients have sent, waiting for someone to look at them."
        action={<span className="tabnum text-sm text-muted">{items?.length ?? 0} waiting</span>}
      />
      {err && <p className="mb-3 text-sm text-status-urgent-fg">{err}</p>}
      {!items && <SkeletonList rows={3} />}
      {items && !items.length && (
        <Empty>
          Nothing waiting. Photos land here as patients send them - the app asks for a set at every
          tray change.
        </Empty>
      )}
      {items && items.length > 0 && (
        <ul className="space-y-3">
          {items.map((p) => (
            <li key={p.id}>
              <PhotoCard photo={p} canReview={can('patient:review_media')} onDone={load} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PhotoCard({ photo, canReview, onDone }: { photo: PendingPhoto; canReview: boolean; onDone: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const review = async (status: 'approved' | 'flagged') => {
    setBusy(status);
    setErr(null);
    try {
      await wfApi.reviewPhoto(photo.id, status, note.trim() || undefined);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  };

  return (
    <Card className="overflow-hidden">
      <div className="grid gap-4 p-4 sm:grid-cols-[minmax(0,16rem)_minmax(0,1fr)]">
        <a
          href={fileDownloadUrl(photo.caseId, photo.fileId, true)}
          target="_blank"
          rel="noreferrer"
          className="block overflow-hidden rounded-lg border border-line bg-surface-2"
        >
          <img
            src={fileDownloadUrl(photo.caseId, photo.fileId, true)}
            alt={`${PHOTO_POSE_LABELS[photo.pose]} for ${photo.patientName}`}
            loading="lazy"
            className="h-56 w-full object-cover"
          />
        </a>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/workflow/cases/${photo.caseId}`} className="font-semibold text-ink hover:underline">
              {photo.patientName}
            </Link>
            <span className="font-mono text-xs text-muted">{photo.caseNumber}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
            <span className="font-medium text-ink">{PHOTO_POSE_LABELS[photo.pose]}</span>
            {photo.trayNo ? <span className="tabnum">Tray {photo.trayNo}</span> : null}
            <span>{formatDateTime(photo.takenAt)}</span>
            {photo.doctorName && <span>{photo.doctorName}</span>}
          </div>
          {photo.note && <p className="mt-2 rounded-md bg-surface-2 px-3 py-2 text-sm text-ink">{photo.note}</p>}

          {err && <p className="mt-2 text-sm text-status-urgent-fg">{err}</p>}

          {canReview ? (
            <div className="mt-3 space-y-2">
              <input
                className={inputCls}
                placeholder="Note (optional) - what the clinic should know"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
              />
              <div className="flex flex-wrap gap-2">
                <Btn tone="accent" disabled={!!busy} onClick={() => void review('approved')}>
                  <IconCheck size={16} />
                  {busy === 'approved' ? 'Saving…' : 'Looks fine'}
                </Btn>
                <Btn tone="ghost" disabled={!!busy} onClick={() => void review('flagged')}>
                  <IconAlert size={16} />
                  {busy === 'flagged' ? 'Saving…' : 'Needs attention'}
                </Btn>
              </div>
              <p className="text-[11px] text-muted">
                The decision is recorded on the case timeline. Flagging doesn&rsquo;t message anyone on
                its own - use the case chat if the clinic needs to act.
              </p>
            </div>
          ) : (
            <p className="mt-3 text-xs text-muted">You don&rsquo;t have permission to review photos.</p>
          )}
        </div>
      </div>
    </Card>
  );
}
